<script setup lang="ts">
import { useRechercheData } from "~/composables/useRechercheData";
import type { RechercheFacets } from "~/services/api/rechercheApi";
import { useRechercheSeo } from "~/composables/useRechercheSeo";
import { useRechercheFilters } from "~/composables/useRechercheFilters";
import { useRechercheFiltering } from "~/composables/useRechercheFiltering";
import { useRechercheLiveSearch } from "~/composables/useRechercheLiveSearch";
import {
  LISTING_ITEMS_PER_PAGE,
  estPageHorsBornes,
  listingPagePath,
  type ValidType,
} from "#shared/utils/listingTypes";

/**
 * ListingView — le corps commun de tous les listings publics.
 *
 * POURQUOI CE COMPOSANT (refonte SEO 2026-08-06) : ce fichier était la page
 * `recherche/[[type]].vue`, une route unique qui servait à la fois D'OUTIL DE
 * RECHERCHE et de RAYON D'ANNUAIRE. Ces deux rôles s'excluent :
 *   - un outil de recherche est dynamique, filtré, à état volatil → il ne doit
 *     PAS être indexé (HelloAsso interdit `/e/recherche` dans son robots.txt) ;
 *   - un rayon d'annuaire doit être stable, paginé et parcourable par Google.
 * Les servir depuis la même URL, c'était mal faire les deux. Mesuré le
 * 2026-08-06 : `/associations` n'exposait que 12 fiches sur 46 dans
 * son HTML serveur, sans aucun lien de pagination → 9 fiches du sitemap
 * étaient « URL is unknown to Google », jamais crawlées.
 *
 * Désormais : un seul corps, monté sur cinq routes.
 *   /associations /cagnottes /evenements /adhesions  → rayons indexables
 *   /recherche                                       → l'outil, noindex
 */
const props = withDefaults(
  defineProps<{
    /** Rayon affiché. « tout » = l'outil de recherche multi-collections. */
    type?: ValidType;
    /**
     * Numéro de page (1-indexé) pour les routes `/xxx/page/N`. Décale le
     * premier fetch : la page N est rendue côté SERVEUR, donc indexable.
     */
    page?: number;
  }>(),
  { type: "tout", page: 1 },
);

const route = useRoute();
const { t } = useI18n({ useScope: "local" });

const activeType = computed<ValidType>(() => props.type);
const currentPage = computed(() => Math.max(1, Math.trunc(props.page) || 1));

// ─── Navigation entre rayons (aussi consommée par le breadcrumb SEO) ──
// Les onglets pointent sur les URLs CANONIQUES des rayons, plus sur
// /recherche/* : le mot que les gens tapent est « associations », pas
// « recherche ». `/recherche` reste l'outil, accessible via la loupe.
const tabs = [
  { key: "tout" as ValidType, label: t('tabs.tout'), to: "/recherche", icon: "i-lucide-grid-2x2" },
  { key: "associations" as ValidType, label: t('tabs.associations'), to: "/recherche/associations", icon: "i-lucide-building-2" },
  { key: "cagnottes" as ValidType, label: t('tabs.cagnottes'), to: "/recherche/cagnottes", icon: "i-lucide-heart" },
  { key: "evenements" as ValidType, label: t('tabs.evenements'), to: "/recherche/billetterie", icon: "i-lucide-calendar-days" },
  { key: "adhesions" as ValidType, label: t('tabs.adhesions'), to: "/recherche/adhesions", icon: "i-lucide-users" },
  { key: "boutiques" as ValidType, label: t('tabs.boutiques'), to: "/recherche/boutiques", icon: "i-lucide-shopping-bag" },
  { key: "qr-codes" as ValidType, label: t('tabs.qrCodes'), to: "/qr", icon: "i-lucide-qr-code" },
];

// ─── Category from URL (alias backward-compat) ───────────────────────
// Conservé pour `useRechercheSeo` qui dérive le SEO title/H1 du paramètre
// URL. La VRAIE source de vérité est désormais `selectedCategories` (init
// depuis l'URL + écrit dans l'URL par le watch global plus bas).
const categoryFromUrl = computed(() => (route.query.category as string) || "");

// ─── Search ──────────────────────────────────────────────────────────
// Karim 2026-06-25 : searchQuery vit dans le state global (composable
// useRechercheSearchQuery). La searchbar a été remontée dans AppHeader sur
// les pages /recherche — la page lit ce state pour filtrer ses listings.
// Défini AVANT useRechercheData car on pousse la recherche à l'API (filtrage
// serveur sur toute la base, pas seulement la page chargée).
const searchQuery = useRechercheSearchQuery();

// Pré-remplissage via ?q= (liens entrants : carte « Association introuvable »,
// partages…). Appliqué au setup (avant useRechercheData) pour que le SSR rende
// directement les résultats filtrés. Karim 2026-08-05.
if (typeof route.query.q === "string" && route.query.q.trim()) {
  searchQuery.value = route.query.q.trim();
}

// ─── Filtres AVANT les données (2026-09-26) ───────────────────────────
// Catégorie, ville, période et prix partent à l'API, qui filtre TOUT le rayon
// avant de paginer. Avant, ils filtraient dans le navigateur la seule page
// chargée : 3 associations sportives affichées sur 16 en production. Les
// filtres sont donc lus d'abord, les données ensuite ; en retour, l'API dit
// ce que contient le rayon (`facetsDuRayon`) pour les listes proposées.
const facetsDuRayon = shallowRef<RechercheFacets | null>(null);

// ─── Modal Filtres + state + persistance URL (composable dédié) ──────
// Regroupe : catégorie, ville, tranche de prix, preset de date, sync URL,
// villes dynamiques (fetch by-city) et sync vers AppHeader.
const {
  showFiltersModal,
  filtresServeur,
  selectedActivityTypes,
  availableActivityTypes,
  selectedCategories,
  selectedVille,
  selectedPriceBucket,
  selectedDatePreset,
  priceMin,
  priceMax,
  dateFrom,
  dateTo,
  effectiveSearchQuery,
  resetAllFilters,
  resetAllFiltersAndQuery,
  activeFilterCount,
  availableCategories,
  availableDatePresets,
  availablePriceBuckets,
  availableCities,
} = await useRechercheFilters({
  activeType,
  facets: facetsDuRayon,
});

// ─── Data fetching (5 collections publiques) ─────────────────────────
const {
  campaignsResponse,
  eventsResponse,
  adhesionsResponse,
  qrCodesResponse,
  organizationsResponse,
  campaigns,
  events,
  adhesions,
  qrCodes,
  organizations,
  shops,
  campaignsTotal,
  eventsTotal,
  adhesionsTotal,
  qrCodesTotal,
  organizationsTotal,
  shopsTotal,
  infiniteByType,
  facets,
  isLoading,
  hasError,
  retryActive,
  ITEMS_PER_SECTION_TOUT,
  // Recherche EFFECTIVE : ignorée quand une catégorie est choisie (le clic
  // sur « Sport » remplit aussi le champ texte avec son libellé — le
  // chercher dans les noms viderait la liste).
} = await useRechercheData(activeType, effectiveSearchQuery, currentPage, filtresServeur);

watch(facets, (v) => (facetsDuRayon.value = v), { immediate: true });

// ─── Pagination parcourable par Google ───────────────────────────────
// Total d'items du rayon actif → nombre de pages → liens <a> réels rendus en
// SSR, plus rel=prev/next. C'est ce qui manquait : sans ces liens, Google ne
// pouvait atteindre que les 12 premiers items de chaque rayon.
const activeTotal = computed(() => {
  const totals: Record<ValidType, number> = {
    tout: 0,
    associations: organizationsTotal.value,
    cagnottes: campaignsTotal.value,
    evenements: eventsTotal.value,
    adhesions: adhesionsTotal.value,
    boutiques: shopsTotal.value,
    "qr-codes": qrCodesTotal.value,
  };
  return totals[activeType.value] ?? 0;
});
const totalPages = computed(() =>
  activeType.value === "tout"
    ? 1
    : Math.max(1, Math.ceil(activeTotal.value / LISTING_ITEMS_PER_PAGE)),
);
// Borne HAUTE de la pagination : au-delà de la dernière page, 404 franc.
// Sans ça, /recherche/associations/page/999 répondait 200 « index, follow »
// avec zéro résultat — une infinité d'URLs vides à explorer, prises sur le
// budget d'exploration qui manque déjà aux fiches d'associations (9 sur 20
// jamais explorées, Search Console du 2026-09-18). La borne BASSE (page < 2)
// est rendue par les fichiers de route `page/[n].vue`.
// `hasError` neutralise la règle : un incident d'API ne doit pas désindexer
// des pages qui existent.
if (estPageHorsBornes(currentPage.value, totalPages.value, hasError.value)) {
  throw createError({ statusCode: 404, statusMessage: t('errors.pageIntrouvable') });
}
// La pagination ne s'affiche PAS quand un filtre client est actif
// (catégorie, ville, prix, date). Bug remonté par Karim 2026-08-08 :
// sur /cagnottes?category=aide_humanitaire une seule cagnotte s'affichait,
// mais une « page 2 » était proposée.
// Cause : `totalPages` se calcule sur le total renvoyé par l'API, qui ignore
// ces filtres — ils sont appliqués côté client, sur les éléments déjà chargés
// (useRechercheFiltering). Les deux comptes ne parlent pas de la même chose.
// La pagination sert le rayon CANONIQUE, celui que Google indexe ; les vues
// filtrées sont des états de l'outil (noindex) où le défilement continu prend
// le relais. La recherche texte, elle, est poussée à l'API : son total est
// juste, on garde donc la pagination dans ce cas.
const showPagination = computed(
  () =>
    activeType.value !== "tout" &&
    totalPages.value > 1 &&
    activeFilterCount.value === 0,
);
const pageHref = (p: number) => listingPagePath(activeType.value, p);
/**
 * Fenêtre de numéros affichés : 1 … n-1 [n] n+1 … dernier.
 * On rend toujours la première et la dernière page pour que Google atteigne
 * les extrémités du rayon en deux sauts, quelle que soit la profondeur.
 */
const pageWindow = computed(() => {
  const last = totalPages.value;
  const cur = currentPage.value;
  const nums = new Set<number>([1, last, cur - 1, cur, cur + 1]);
  return [...nums].filter((n) => n >= 1 && n <= last).sort((a, b) => a - b);
});

// ─── SEO per type (composable encapsule useSeoMeta + useHead + JSON-LD) ──
useRechercheSeo({
  activeType,
  categoryFromUrl,
  currentPage,
  totalPages,
  campaignsTotal,
  eventsTotal,
  adhesionsTotal,
  qrCodesTotal,
  organizationsTotal,
  shopsTotal,
  campaigns,
  events,
  adhesions,
  organizations,
  tabs,
});

// ─── Hero content per type ───────────────────────────────────────────
const heroConfig = computed(() => {
  const map: Record<ValidType, { placeholder: string; h1: string; intro: string }> = {
    tout: {
      placeholder: t('hero.tout.placeholder'),
      h1: t('hero.tout.h1'),
      intro: t('hero.tout.intro'),
    },
    boutiques: {
      placeholder: t('hero.boutiques.placeholder'),
      h1: t('hero.boutiques.h1'),
      intro: t('hero.boutiques.intro'),
    },
    cagnottes: {
      placeholder: t('hero.cagnottes.placeholder'),
      h1: t('hero.cagnottes.h1'),
      intro: t('hero.cagnottes.intro'),
    },
    evenements: {
      placeholder: t('hero.evenements.placeholder'),
      h1: t('hero.evenements.h1'),
      intro: t('hero.evenements.intro'),
    },
    adhesions: {
      placeholder: t('hero.adhesions.placeholder'),
      h1: t('hero.adhesions.h1'),
      intro: t('hero.adhesions.intro'),
    },
    associations: {
      placeholder: t('hero.associations.placeholder'),
      h1: t('hero.associations.h1'),
      intro: t('hero.associations.intro'),
    },
    "qr-codes": {
      placeholder: t('hero.qrCodes.placeholder'),
      h1: t('hero.qrCodes.h1'),
      intro: t('hero.qrCodes.intro'),
    },
  };
  return map[activeType.value];
});


// ─── Résultats live pour le panel de recherche (debounced) ──
// Alimente le state partagé useRechercheLiveResults() consommé par la
// searchbar (header desktop ET version mobile rendue dans cette page).
useRechercheLiveSearch({ organizations, events, adhesions, campaigns });
const rechercheLiveResults = useRechercheLiveResults();

// ─── Filtered data (client-side search + category — composable dédié) ──
const {
  filteredCampaigns,
  filteredEvents,
  filteredAdhesions,
  filteredQRCodes,
  filteredOrganizations,
  filteredShops,
} = useRechercheFiltering({
  activeType,
  campaigns,
  events,
  adhesions,
  qrCodes,
  organizations,
  shops,
  effectiveSearchQuery,
  selectedCategories,
  selectedActivityTypes,
  selectedVille,
  priceMin,
  priceMax,
  dateFrom,
  dateTo,
});

// ─── Infinite scroll (remplace la pagination par boutons) ────────────
// Un seul type est actif à la fois → on branche la sentinelle sur les handles
// (hasNext/isFetchingNext/fetchNext) du type courant. En mode "tout", pas
// d'infinite scroll (chaque section est une preview de 6 items).
const activeInfinite = computed(() =>
  activeType.value === "tout" ? null : infiniteByType[activeType.value],
);
const infiniteHasMore = computed(() => activeInfinite.value?.hasNext.value ?? false);
const infiniteLoading = computed(() => activeInfinite.value?.isFetchingNext.value ?? false);
// Une page suivante a échoué (réseau) : la sentinelle affiche « Réessayer »
// SANS effacer la liste déjà chargée. Karim 2026-07-23.
const infiniteError = computed(() => activeInfinite.value?.nextError.value ?? false);
const loadMore = () => activeInfinite.value?.fetchNext();

// Repartir en haut de liste à chaque bascule de type (sinon on reste à
// mi-scroll de la liste précédente).
if (import.meta.client) {
  watch(activeType, () => window.scrollTo({ top: 0, behavior: "instant" }));
}

// ─── "Tout" mode section checks ─────────────────────────────────────
// eslint-disable-next-line no-restricted-syntax -- listes filtrées localement pour la page courante
const hasOrgsInTout = computed(() => activeType.value === "tout" && filteredOrganizations.value.length > 0);
// eslint-disable-next-line no-restricted-syntax -- liste filtrée localement
const hasCampaignsInTout = computed(() => activeType.value === "tout" && filteredCampaigns.value.length > 0);
// eslint-disable-next-line no-restricted-syntax -- liste filtrée localement
const hasEventsInTout = computed(() => activeType.value === "tout" && filteredEvents.value.length > 0);
// eslint-disable-next-line no-restricted-syntax -- liste filtrée localement
const hasAdhesionsInTout = computed(() => activeType.value === "tout" && filteredAdhesions.value.length > 0);
// eslint-disable-next-line no-restricted-syntax -- liste filtrée localement
const hasShopsInTout = computed(() => activeType.value === "tout" && filteredShops.value.length > 0);
// eslint-disable-next-line no-restricted-syntax -- liste filtrée localement
const hasQRCodesInTout = computed(() => activeType.value === "tout" && filteredQRCodes.value.length > 0);

const noResultsInTout = computed(() => {
  if (activeType.value !== "tout") return false;
  if (isLoading.value) return false;
  // Tant qu'AU MOINS une des 5 réponses n'est pas encore arrivée, on ne déclare
  // pas "aucun résultat" — c'est encore en cours de chargement.
  if (
    !campaignsResponse.value ||
    !eventsResponse.value ||
    !adhesionsResponse.value ||
    !qrCodesResponse.value ||
    !organizationsResponse.value
  ) {
    return false;
  }
  return (
    !hasOrgsInTout.value &&
    !hasCampaignsInTout.value &&
    !hasEventsInTout.value &&
    !hasAdhesionsInTout.value &&
    !hasShopsInTout.value &&
    !hasQRCodesInTout.value
  );
});

// Empty state for single-type views
// Important : on vérifie `*Response.value` (data brute du fetch) au lieu de `filtered*`,
// car le ref miroir (`campaigns`, `events`, etc.) est alimenté par un watcher
// asynchrone — ce qui causerait un flash "aucun résultat" entre la résolution
// du fetch et le tick du watcher (notamment au hard refresh + hydratation).
const noResultsSingle = computed(() => {
  if (activeType.value === "tout") return false;
  if (isLoading.value) return false;
  if (activeType.value === "associations") {
    const arr = organizationsResponse.value?.data;
    if (!arr) return false;
    // eslint-disable-next-line no-restricted-syntax -- liste filtrée localement
    return filteredOrganizations.value.length === 0;
  }
  if (activeType.value === "cagnottes") {
    const arr = campaignsResponse.value?.data;
    if (!arr) return false;
    // eslint-disable-next-line no-restricted-syntax -- liste filtrée localement
    return filteredCampaigns.value.length === 0;
  }
  if (activeType.value === "evenements") {
    const arr = eventsResponse.value?.data;
    if (!arr) return false;
    // eslint-disable-next-line no-restricted-syntax -- liste filtrée localement
    return filteredEvents.value.length === 0;
  }
  if (activeType.value === "adhesions") {
    const arr = adhesionsResponse.value?.data;
    if (!arr) return false;
    // eslint-disable-next-line no-restricted-syntax -- liste filtrée localement
    return filteredAdhesions.value.length === 0;
  }
  if (activeType.value === "qr-codes") {
    const arr = qrCodesResponse.value?.data;
    if (!arr) return false;
    // eslint-disable-next-line no-restricted-syntax -- liste filtrée localement
    return filteredQRCodes.value.length === 0;
  }
  return false;
});

const emptyStateConfig = computed(() => {
  const map: Record<ValidType, { icon: string; title: string }> = {
    tout: { icon: "i-lucide-search", title: t('emptyState.tout.title') },
    associations: { icon: "i-lucide-building-2", title: t('emptyState.associations.title') },
    cagnottes: { icon: "i-lucide-heart", title: t('emptyState.cagnottes.title') },
    evenements: { icon: "i-lucide-calendar-x", title: t('emptyState.evenements.title') },
    adhesions: { icon: "i-lucide-users", title: t('emptyState.adhesions.title') },
    boutiques: { icon: "i-lucide-shopping-bag", title: t('emptyState.boutiques.title') },
    "qr-codes": { icon: "i-lucide-qr-code", title: t('emptyState.qrCodes.title') },
  };
  return map[activeType.value];
});
</script>
<template>
  <div class="min-h-screen bg-white">
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- Top bar: search + filters + tabs -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <div class="pt-36 sm:pt-40 lg:pt-34 pb-2 px-4 bg-[#fafcff] border-b border-gray-200">
      <div class="max-w-6xl mx-auto">
        <!-- SEO heading + intro (rendered SSR for indexability) -->
        <div class="mb-8 sm:mb-10 lg:mb-12 text-center">
          <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {{ heroConfig.h1 }}
          </h1>
          <p class="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {{ heroConfig.intro }}
          </p>
        </div>

        <!-- Searchbar MOBILE (Karim 2026-07-09) : rendue ici en md:hidden,
             au-dessus des tabs. Sur desktop la searchbar vit dans AppHeader
             (hidden md:flex) → sous md le header ne l'affiche pas. Elle lit les
             MÊMES states partagés (searchQuery / cities / selectedVille /
             liveResults) que la version header → cohérence garantie.
             Pas de bouton Filtres sur mobile (Karim 2026-07-09) : il reste dans
             le slot trailing des tabs, visible uniquement en desktop. -->
        <div class="md:hidden mb-4">
          <RechercheGlobalSearch
            :cities="availableCities"
            :selected-ville="selectedVille"
            :live-results="rechercheLiveResults"
            @select-ville="(slug) => (selectedVille = slug)"
          />
        </div>

        <!-- Tabs + bouton Filtres (desktop uniquement, Karim 2026-06-25).
             Le bouton est injecté via le slot `trailing` du RechercheTabsBar
             (ml-auto le pousse à droite). Masqué sur mobile (hidden md:flex)
             car la rangée d'onglets déborde en overflow-x-auto et poussait le
             bouton hors écran ; sur mobile on ne montre que la searchbar. -->
        <!-- Bouton « Filtres » retiré pour le moment (Karim 2026-09-26) : la
             barre de recherche de l'en-tête (quoi + où + thèmes) fait déjà le
             travail, deux entrées pour la même chose brouillaient la page. La
             fenêtre RechercheFiltersModal reste en place, prête à revenir.
        <RechercheTabsBar :active-type="activeType">
          <template #trailing>
            <button
              type="button"
              class="hidden md:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors bg-main text-white hover:opacity-90 shadow-sm"
              @click="showFiltersModal = true"
            >
              <UIcon name="i-lucide-sliders-horizontal" class="size-4" />
              <span>Filtres</span>
              <span
                v-if="activeFilterCount > 0"
                class="size-5 flex items-center justify-center rounded-full bg-white/25 text-[10px] font-bold"
              >
                {{ activeFilterCount }}
              </span>
            </button>
          </template>
        </RechercheTabsBar>
        -->
        <RechercheTabsBar :active-type="activeType" />

        <RechercheFiltersModal
          v-model:open="showFiltersModal"
          v-model:selected-categories="selectedCategories"
          v-model:selected-activity-types="selectedActivityTypes"
          v-model:selected-price-bucket="selectedPriceBucket"
          v-model:selected-date-preset="selectedDatePreset"
          :active-type="activeType"
          :categories="availableCategories"
          :activity-types="availableActivityTypes"
          :cities="availableCities"
          :selected-ville="selectedVille"
          :price-buckets="availablePriceBuckets"
          :date-presets="availableDatePresets"
          @select-ville="(slug) => (selectedVille = slug)"
          @clear-ville="selectedVille = null"
          @reset-all="resetAllFilters"
        />
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- Content -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <div class="px-4 py-6">
      <div class="max-w-6xl mx-auto">
        <Transition
          mode="out-in"
          enter-active-class="transition-all duration-250 ease-out"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-2"
        >
        <div :key="activeType">
        <!-- Error State (page 1 en échec : rien à afficher) -->
        <div v-if="hasError" class="text-center py-20">
          <div class="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <UIcon name="i-lucide-alert-circle" class="size-8 text-red-400" />
          </div>
          <h3 class="text-lg font-bold text-gray-900 mb-1">
            {{ t('error.title') }}
          </h3>
          <p class="text-gray-500 text-sm mb-5">
            {{ t('error.message') }}
          </p>
          <button
            type="button"
            class="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-gray-200 bg-white text-sm font-semibold text-(--color-ink) hover:border-[#0058fb]/40 hover:shadow-sm transition-all"
            @click="retryActive"
          >
            <UIcon name="i-lucide-rotate-ccw" class="w-4 h-4" />
            {{ t('error.retry') }}
          </button>
        </div>

        <!-- ═════════════════════════════════════════════════════════ -->
        <!-- "Tout" mode: sections with headers -->
        <!-- ═════════════════════════════════════════════════════════ -->
        <template v-else-if="activeType === 'tout'">
          <!-- Associations Section -->
          <div v-if="hasOrgsInTout" class="mb-10">
            <RechercheSectionHeader :title="t('sections.associations')" :total="organizationsTotal" to="/recherche/associations" />
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <RechercheOrganizationCard
                v-for="(org, i) in filteredOrganizations.slice(0, ITEMS_PER_SECTION_TOUT)"
                :key="org.id"
                :org="org"
                :priority="i < 3"
              />
            </div>
          </div>

          <!-- Cagnottes Section -->
          <div v-if="hasCampaignsInTout" class="mb-10">
            <RechercheSectionHeader :title="t('sections.cagnottes')" :total="campaignsTotal" to="/recherche/cagnottes" />
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <RechercheCagnotteCard
                v-for="campaign in filteredCampaigns.slice(0, ITEMS_PER_SECTION_TOUT)"
                :key="campaign.id"
                :campaign="campaign"
              />
            </div>
          </div>

          <!-- Events Section -->
          <div v-if="hasEventsInTout" class="mb-10">
            <RechercheSectionHeader :title="t('sections.evenements')" :total="eventsTotal" to="/recherche/billetterie" />
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <RechercheEventCard
                v-for="event in filteredEvents.slice(0, ITEMS_PER_SECTION_TOUT)"
                :key="event.id"
                :event="event"
              />
            </div>
          </div>

          <!-- Adhesions Section -->
          <div v-if="hasAdhesionsInTout" class="mb-10">
            <RechercheSectionHeader :title="t('sections.adhesions')" :total="adhesionsTotal" to="/recherche/adhesions" />
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <RechercheAdhesionCard
                v-for="plan in filteredAdhesions.slice(0, ITEMS_PER_SECTION_TOUT)"
                :key="plan.id"
                :plan="plan"
              />
            </div>
          </div>

          <!-- Boutiques Section -->
          <div v-if="hasShopsInTout" class="mb-10">
            <RechercheSectionHeader :title="t('sections.boutiques')" :total="shopsTotal" to="/recherche/boutiques" />
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <RechercheBoutiqueCard
                v-for="shop in filteredShops.slice(0, ITEMS_PER_SECTION_TOUT)"
                :key="shop.id"
                :shop="shop"
              />
            </div>
          </div>

          <!-- QR Codes Section -->
          <div v-if="hasQRCodesInTout" class="mb-10">
            <RechercheSectionHeader :title="t('sections.qrCodes')" :total="qrCodesTotal" to="/qr" />
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <RechercheQrCodeCard
                v-for="qr in filteredQRCodes.slice(0, ITEMS_PER_SECTION_TOUT)"
                :key="qr.id"
                :qr="qr"
              />
            </div>
          </div>

          <!-- Empty state for "tout" -->
          <RechercheEmptyState
            v-if="noResultsInTout"
            :has-query="!!searchQuery"
            :has-filters="activeFilterCount > 0"
            @reset="resetAllFiltersAndQuery"
          />
        </template>

        <!-- ═════════════════════════════════════════════════════════ -->
        <!-- Single type views -->
        <!-- ═════════════════════════════════════════════════════════ -->
        <template v-else>
          <!-- ── Associations ── -->
          <div
            v-if="activeType === 'associations' && filteredOrganizations.length > 0"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            <RechercheOrganizationCard
              v-for="(org, i) in filteredOrganizations"
              :key="org.id"
              :org="org"
              :priority="i < 3"
            />
          </div>

          <!-- ── Cagnottes ── -->
          <div
            v-if="activeType === 'cagnottes' && filteredCampaigns.length > 0"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            <RechercheCagnotteCard
              v-for="campaign in filteredCampaigns"
              :key="campaign.id"
              :campaign="campaign"
            />
          </div>

          <!-- ── Boutiques ── -->
          <div
            v-if="activeType === 'boutiques' && filteredShops.length > 0"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            <RechercheBoutiqueCard
              v-for="shop in filteredShops"
              :key="shop.id"
              :shop="shop"
            />
          </div>

          <!-- ── Evenements ── -->
          <div
            v-if="activeType === 'evenements' && filteredEvents.length > 0"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            <RechercheEventCard
              v-for="event in filteredEvents"
              :key="event.id"
              :event="event"
            />
          </div>

          <!-- ── Adhesions ── -->
          <div
            v-if="activeType === 'adhesions' && filteredAdhesions.length > 0"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            <RechercheAdhesionCard
              v-for="plan in filteredAdhesions"
              :key="plan.id"
              :plan="plan"
            />
          </div>

          <!-- ── QR Codes ── -->
          <div
            v-if="activeType === 'qr-codes' && filteredQRCodes.length > 0"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            <RechercheQrCodeCard
              v-for="qr in filteredQRCodes"
              :key="qr.id"
              :qr="qr"
            />
          </div>

          <!-- ── Scroll infini — UNIQUEMENT quand la pagination boutons n'est
               PAS affichée (vues filtrées / recherche). Sur la vue canonique
               paginée, scroll infini + boutons /page/N se chevauchaient : la
               page 1 chargeait offset 0,12,24… puis « page 2 » (offset 12)
               réaffichait les mêmes assos. Un SEUL paradigme par vue :
               pagination boutons OU scroll infini, jamais les deux.
               Karim 2026-08-15. -->
          <InfiniteScrollSentinel
            v-if="!noResultsSingle && !showPagination"
            :has-more="infiniteHasMore"
            :loading="infiniteLoading"
            :error="infiniteError"
            @load="loadMore"
          />

          <!-- ── Pagination PARCOURABLE (SEO) ──────────────────────────
               De VRAIS <a href> rendus côté serveur, en plus du scroll
               infini qui reste le confort de lecture. C'est ce qui manquait :
               sans ces liens, Google ne pouvait atteindre que les 12 premiers
               items de chaque rayon (audit 2026-08-06 — 9 fiches du sitemap
               n'avaient jamais été crawlées). Chemins `/page/N` et non
               `?page=N` : les paramètres de requête sont plus souvent ignorés
               et n'accumulent pas d'autorité. -->
          <nav
            v-if="showPagination"
            class="mt-10 flex items-center justify-center gap-2 flex-wrap"
            :aria-label="t('pagination.ariaLabel')"
          >
            <NuxtLink
              v-if="currentPage > 1"
              :to="pageHref(currentPage - 1)"
              rel="prev"
              class="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50"
            >
              {{ t('pagination.precedent') }}
            </NuxtLink>

            <template v-for="(n, i) in pageWindow" :key="n">
              <span
                v-if="i > 0 && n - (pageWindow[i - 1] ?? 0) > 1"
                class="px-1 text-gray-400 select-none"
                aria-hidden="true"
              >…</span>
              <NuxtLink
                :to="pageHref(n)"
                :aria-current="n === currentPage ? 'page' : undefined"
                class="min-w-10 px-3 py-2 rounded-lg text-sm font-semibold text-center border transition-colors"
                :class="
                  n === currentPage
                    ? 'bg-main text-white! border-transparent'
                    : 'text-gray-700 border-gray-200 hover:bg-gray-50'
                "
              >
                {{ n }}
              </NuxtLink>
            </template>

            <NuxtLink
              v-if="currentPage < totalPages"
              :to="pageHref(currentPage + 1)"
              rel="next"
              class="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50"
            >
              {{ t('pagination.suivant') }}
            </NuxtLink>
          </nav>

          <!-- ── Empty state (single-type views) ── -->
          <RechercheEmptyState
            v-if="noResultsSingle"
            :icon="emptyStateConfig.icon"
            :title="emptyStateConfig.title"
            :has-query="!!searchQuery"
            :has-filters="activeFilterCount > 0"
            :empty-description="t('emptyState.description')"
            @reset="resetAllFiltersAndQuery"
          />
        </template>
        </div>
        </Transition>
      </div>
    </div>

    <!-- CTA Banner -->
    <CtaBanner />
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/components/listing/ListingView.json"></i18n>
